import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { StatusBar } from 'expo-status-bar';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ScreenHeader } from '../components/ScreenHeader';
import { colors, screenPadding, spacing, typography } from '../theme/tokens';
import { useGabarit } from '../hooks/useGabarit';
import { t } from '../i18n';
import { ouvrirLien } from '../utils/ouvrirLien';

const CGU_CONTACT = 'info@alternjob.com';

// Lien mailto cliquable (Text imbriqué dans Text — autorisé en RN).
function Mail() {
  return (
    <Text style={styles.link} onPress={() => ouvrirLien(`mailto:${CGU_CONTACT}`)}>
      {CGU_CONTACT}
    </Text>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <Text style={styles.paragraph}>{children}</Text>;
}

function Bullets({ items }: { items: string[] }) {
  return (
    <View style={styles.bullets}>
      {items.map((it, i) => (
        <View key={i} style={styles.bulletRow}>
          <Text style={styles.bulletDot}>•</Text>
          <Text style={styles.bulletText}>{it}</Text>
        </View>
      ))}
    </View>
  );
}

// Conditions Générales d'Utilisation — version 1.5 (texte validé).
export function TermsScreen({ onBack, onOpenPrivacy }: { onBack?: () => void; onOpenPrivacy?: () => void }) {
  const { large } = useGabarit();
  // Renvoi cliquable vers la Politique de confidentialité.
  const Privacy = ({ label = 'la Politique de confidentialité' }: { label?: string }) => (
    <Text style={styles.link} onPress={() => onOpenPrivacy?.()}>
      {label}
    </Text>
  );

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {/* Fond clair : icônes foncées. Sur la pile d'avant connexion, cette page s'ouvre par-dessus la
          connexion, dont la barre est claire ; StatusBar empile ses réglages et la rend au retour. */}
      <StatusBar style="dark" />
      <ScreenHeader onBack={onBack} title={t('legalHeaders.terms')} testID="terms-back" />
      <ScrollView contentContainerStyle={[styles.content, large && styles.contentL]}>
        <Text style={styles.docTitle}>CONDITIONS GÉNÉRALES D’UTILISATION - HORMÉIA</Text>
        <Text style={styles.docSubtitle}>Version 1.12 - En vigueur au 28/10/2026</Text>

        <Section title="Préambule">
          <P>
            Bienvenue sur Horméia (« l’Application »). Les présentes Conditions Générales d’Utilisation
            (« CGU ») définissent les règles d’accès et d’usage de l’Application, éditée par Alternjob, SAS au
            capital de 1000 €, immatriculée au RCS de Nanterre sous le numéro 892 356 965, dont le siège social
            est situé au 34 route des Gardes, 92190 Meudon et représentée par Julien Dauzats, en sa qualité de
            Président (« l’Éditeur »,
            « nous »).
          </P>
          <P>Directeur de la publication : Julien Dauzats</P>
          <P>Contact : <Mail /></P>
          <P>Téléphone : 06 60 82 50 10</P>
          <P>
            Hébergeur : Supabase Pte. Ltd., 65 Chulia Street #38-02/03, OCBC Centre, Singapour 049513
            (legal@supabase.io), qui héberge les données dans l’Union européenne (région de Stockholm, Suède) chez
            Amazon Web Services EMEA SARL, 38 avenue John F. Kennedy, L-1855 Luxembourg.
          </P>
          <P>Numéro de TVA intracommunautaire : FR14892356965</P>
          <P>
            En créant un compte, tu reconnais avoir lu, compris et accepté sans réserve les présentes CGU ainsi
            que <Privacy />, qui en fait partie intégrante. Si tu n’acceptes pas ces CGU, tu ne peux pas
            utiliser l’Application.
          </P>
        </Section>

        <Section title="Article 1 - Objet du service">
          <P>
            Horméia est un service d’accompagnement et de préparation à la recherche d’alternance ou d’emploi.
            L’Application propose notamment :
          </P>
          <Bullets
            items={[
              'la notation, l’analyse et l’aide à la création de CV ;',
              'l’audit de profil LinkedIn et des recommandations d’optimisation ;',
              'la simulation d’entretien et un retour structuré sur tes réponses ;',
              'la recherche d’offres d’alternance et d’emploi publiées par France Travail, La bonne alternance et Adzuna, et la préparation par l’IA d’une candidature pour une offre (CV adapté, lettre) ;',
              'un coaching et des contenus de préparation à la candidature.',
            ]}
          />
          <P>
            Ce que Horméia n’est pas. L’Application est un outil d’aide à la préparation. Elle ne constitue ni
            un service de placement, ni une agence d’emploi, ni un cabinet de recrutement, ni un organisme de
            formation. L’Éditeur n’intervient à aucun titre dans la décision de recrutement d’un employeur.
          </P>
          <P>
            Absence de garantie de résultat. L’Éditeur est tenu à une obligation de moyens. Il ne garantit en
            aucun cas l’obtention d’un entretien, d’un contrat d’alternance, d’un stage ou d’un emploi, ni un
            quelconque niveau de score, de visibilité ou de réponse des recruteurs.
          </P>
        </Section>

        <Section title="Article 2 - Accès au service et conditions d’inscription">
          <Text style={styles.subTitle}>2.1 Capacité et âge minimum</Text>
          <P>
            L’Application est accessible aux personnes physiques âgées de 15 ans révolus au minimum.
          </P>
          <Bullets
            items={[
              'Si tu as moins de 18 ans, tu déclares avoir obtenu l’autorisation préalable du ou des titulaires de l’autorité parentale, qui acceptent les présentes CGU en ton nom et pour ton compte. L’Éditeur se réserve le droit de demander tout justificatif.',
              'Si tu as moins de 15 ans, l’inscription est interdite, le traitement de tes données à caractère personnel dans le cadre d’un service de la société de l’information nécessitant le consentement du titulaire de l’autorité parentale (art. 8 du RGPD et art. 45 de la loi Informatique et Libertés).',
            ]}
          />
          <P>
            Tout compte dont il apparaîtrait qu’il a été créé en violation du présent article sera suspendu puis
            supprimé.
          </P>
          <Text style={styles.subTitle}>2.2 Création de compte</Text>
          <P>
            L’accès aux fonctionnalités suppose la création d’un compte personnel via email et mot de passe,
            connexion Google ou connexion Apple. Tu t’engages à fournir des informations exactes, complètes et à
            jour, et à les actualiser en cas de changement.
          </P>
          <Text style={styles.subTitle}>2.3 Sécurité des identifiants</Text>
          <P>
            Tes identifiants sont strictement personnels et confidentiels. Tu es responsable de leur
            conservation et de toute activité effectuée depuis ton compte. En cas d’usage non autorisé ou de
            suspicion de compromission, tu dois nous en informer sans délai à <Mail />. Un compte ne peut être
            ni partagé, ni cédé, ni revendu.
          </P>
        </Section>

        <Section title="Article 3 - Utilisation de l’intelligence artificielle">
          <Text style={styles.subTitle}>3.1 Information sur la nature du service</Text>
          <P>
            Tu es informé(e) que tu interagis avec un système d’intelligence artificielle. Horméia s’appuie sur
            des modèles d’IA générative, notamment le modèle Claude, fourni par Anthropic, pour analyser les
            documents et informations que tu transmets (CV, profil LinkedIn, réponses d’entretien) et générer
            des contenus, scores, analyses et recommandations (les « Résultats IA »).
          </P>
          <Text style={styles.subTitle}>3.2 Limites intrinsèques de l’IA</Text>
          <P>Les systèmes d’IA générative présentent des limites connues. Ils peuvent :</P>
          <Bullets
            items={[
              'produire des informations inexactes, obsolètes, incomplètes ou inventées (« hallucinations ») ;',
              'générer des analyses ou scores variables pour un même document ;',
              'refléter des biais issus de leurs données d’entraînement ;',
              'proposer des formulations non adaptées à ton secteur, ton parcours ou ton projet.',
            ]}
          />
          <Text style={styles.subTitle}>3.3 Caractère indicatif et rôle de l’utilisateur</Text>
          <P>
            Les Résultats IA sont fournis à titre purement indicatif et non contraignant. Ils ne constituent ni
            un conseil professionnel personnalisé, ni un conseil juridique, ni une décision opposable. Tu restes
            seul(e) responsable du contenu final de ton CV, de ton profil, de tes lettres et de tes
            candidatures. Il t’appartient de relire, vérifier, corriger et valider tout contenu proposé par
            l’IA avant toute utilisation, diffusion ou envoi à un tiers. En particulier, tu t’engages à ne
            jamais laisser figurer dans tes documents de candidature une information fausse, exagérée ou non
            vérifiée générée par l’Application.
          </P>
          <Text style={styles.subTitle}>3.4 Absence de décision automatisée à effet juridique</Text>
          <P>
            Les scores et analyses produits par l’Application n’ont aucun effet sur tes chances réelles
            d’embauche et ne sont jamais transmis automatiquement à un employeur ou à un recruteur. Ils ne
            constituent pas une décision automatisée au sens de l’article 22 du RGPD.
          </P>
          <Text style={styles.subTitle}>3.5 Contenus transmis aux fournisseurs d’IA</Text>
          <P>
            Les contenus que tu soumets sont transmis aux fournisseurs de modèles d’IA aux seules fins de
            générer les Résultats IA. Les conditions de ce traitement (localisation, durée de conservation,
            absence d’utilisation pour l’entraînement des modèles) sont détaillées dans <Privacy />.
          </P>
          <P>
            Recommandation : ne transmets pas via l’Application d’informations sensibles dont tu ne souhaites pas
            qu’elles figurent dans tes documents de candidature (état de santé, appartenance syndicale, opinions
            politiques ou religieuses, orientation sexuelle, etc.).
          </P>
        </Section>

        {/* « 3 bis » plutôt qu'un renumérotage : les articles suivants gardent leur numéro, et les
            renvois qui les citent restent justes. */}
        <Section title="Article 3 bis - Offres d’emploi">
          <P>
            Les offres sont publiées par des tiers (France Travail, La bonne alternance, Adzuna et leurs partenaires).
            L’Éditeur les affiche telles qu’il les reçoit, avec leur source et leur date de publication ou de
            mise à jour. Horméia ne garantit pas le contenu des offres publiées par des tiers : ni leur
            exactitude, ni leur disponibilité, ni l’issue d’une candidature. Une offre peut être retirée par le
            recruteur à tout moment.
          </P>
          <P>
            La consultation des offres est gratuite. La préparation d’une candidature par l’IA est comptée dans
            les « candidatures IA » de ton offre ; une préparation qui n’aboutit pas n’est pas comptée. Le
            dossier préparé est une proposition : tu restes responsable de son contenu et de sa vérification
            avant tout usage.
          </P>
          <P>
            Chaque offre porte la mention de sa source. Les offres issues de France Travail sont réutilisées
            sous la licence de réutilisation de la base des offres d’emploi de France Travail. Celles de La
            bonne alternance proviennent de l’API Apprentissage de l’État (licence ouverte Etalab 2.0). Celles
            d’Adzuna portent la mention « Jobs by Adzuna » : seul un extrait est affiché, l’annonce complète se
            consulte sur adzuna.fr, sous les conditions d’Adzuna.
          </P>
        </Section>

        <Section title="Article 4 - Contenus utilisateur">
          <Text style={styles.subTitle}>4.1 Définition</Text>
          <P>
            On entend par « Contenus Utilisateur » l’ensemble des éléments que tu transmets via l’Application :
            CV, textes, réponses d’entretien, liens de profil, documents et données personnelles.
          </P>
          <Text style={styles.subTitle}>4.2 Garanties</Text>
          <P>Tu garantis que tes Contenus Utilisateur :</P>
          <Bullets
            items={[
              'sont exacts et te concernent personnellement ;',
              'ne portent atteinte à aucun droit de tiers (droit d’auteur, droit à l’image, secret des affaires, données personnelles de tiers) ;',
              'ne comportent aucun contenu illicite, diffamatoire, haineux, discriminatoire, violent ou à caractère sexuel ;',
              'ne contiennent pas de code malveillant.',
            ]}
          />
          <P>
            Tu t’engages notamment à ne pas téléverser le CV ou les données personnelles d’une autre personne
            sans son accord.
          </P>
          <Text style={styles.subTitle}>4.3 Licence d’utilisation</Text>
          <P>
            Tu restes propriétaire de tes Contenus Utilisateur. Tu concèdes à l’Éditeur une licence non
            exclusive, gratuite, mondiale et limitée à la durée de ton inscription, aux seules fins d’héberger,
            traiter et afficher ces contenus pour te fournir le service. Cette licence n’autorise pas l’Éditeur
            à publier, revendre ou exploiter commercialement tes contenus, ni à les utiliser pour entraîner des
            modèles d’IA.
          </P>
        </Section>

        <Section title="Article 5 - Obligations et usages interdits">
          <P>
            Tu t’engages à utiliser l’Application de bonne foi et conformément à la loi. Sont notamment
            interdits :
          </P>
          <Bullets
            items={[
              'le scraping, l’extraction automatisée, la rétro-ingénierie, la décompilation ou la copie du code ou des bases de données ;',
              'l’usage de bots, scripts ou requêtes automatisées ;',
              'la revente, la sous-licence ou la mise à disposition de l’Application à des tiers ;',
              'toute tentative d’injection de prompt visant à détourner le système d’IA de sa finalité, à en extraire les instructions système, ou à lui faire produire des contenus illicites ;',
              'l’usage de l’Application pour produire des candidatures frauduleuses ou mensongères, ou pour usurper l’identité d’un tiers ;',
              'toute action de nature à porter atteinte à la sécurité, à la disponibilité ou à l’intégrité du service ;',
              'un usage manifestement excessif au regard d’un usage personnel normal.',
            ]}
          />
          <P>
            En cas de manquement, l’Éditeur pourra suspendre ou résilier ton compte dans les conditions de
            l’article 10.
          </P>
        </Section>

        <Section title="Article 6 - Propriété intellectuelle">
          <P>
            L’Application, sa marque « Horméia », son nom de domaine, ses interfaces, sa charte graphique, ses
            textes, ses bases de données, ses algorithmes, ses grilles d’évaluation et ses prompts sont la
            propriété exclusive de l’Éditeur ou font l’objet d’une licence à son profit. Ils sont protégés par
            le Code de la propriété intellectuelle. L’Éditeur te concède un droit d’usage personnel, non
            exclusif, non cessible et révocable, strictement limité à ton usage personnel et non commercial,
            pour la durée de ton inscription. Toute reproduction, représentation, adaptation ou exploitation,
            totale ou partielle, non expressément autorisée, constitue une contrefaçon. Les contenus générés
            pour toi par l’IA (CV, formulations, recommandations) te sont cédés pour ton usage personnel de
            candidature. Tu es informé(e) qu’en l’état du droit français, un contenu exclusivement généré par
            une IA n’est pas nécessairement protégeable par le droit d’auteur.
          </P>
        </Section>

        <Section title="Article 7 - Conditions financières">
          <Text style={styles.subTitle}>7.1 Offres</Text>
          <P>
            L’Application propose une version gratuite ainsi que des abonnements payants dont les
            caractéristiques et tarifs (TTC) sont présentés dans l’Application avant tout engagement.
          </P>
          <P>
            La consultation des offres de l’onglet Offres est gratuite pour tous. Les quotas mensuels de chaque
            formule, dont les candidatures IA de l’onglet Offres, sont présentés dans l’Application avant tout
            engagement.
          </P>
          <Text style={styles.subTitle}>7.2 Paiement</Text>
          <P>
            Les paiements sont réalisés via l’App Store d’Apple ou Google Play pour les abonnements et via
            Stripe pour le coaching, prestataires de paiement dont les conditions s’appliquent. L’Éditeur n’a jamais accès à
            tes coordonnées bancaires complètes.
          </P>
          <Text style={styles.subTitle}>7.3 Abonnements et reconduction</Text>
          <P>
            Les abonnements sont conclus pour une durée d’un mois ou d’un an, selon la formule choisie, et
            reconduits tacitement pour la même durée, sauf résiliation avant la fin de la période en cours. Tu
            peux résilier à tout moment depuis la gestion des abonnements de l’App Store ou de Google Play, la
            résiliation prenant effet à l’échéance de la période en cours.
          </P>
          <Text style={styles.subTitle}>7.4 Droit de rétractation</Text>
          <P>
            Tu disposes d’un délai de 14 jours à compter de la conclusion du contrat pour exercer ton droit de
            rétractation, sans motif ni pénalité (art. L221-18 du Code de la consommation), via le formulaire
            figurant en annexe ou à l’adresse <Mail />.
          </P>
          <Text style={styles.subTitle}>7.5 Modification tarifaire</Text>
          <P>
            Toute évolution tarifaire sera notifiée au moins 30 jours avant son entrée en vigueur. Elle ne
            s’applique qu’aux périodes d’abonnement postérieures ; tu peux résilier sans frais avant sa prise
            d’effet.
          </P>
        </Section>

        <Section title="Article 8 - Données personnelles">
          <P>
            Le traitement de tes données à caractère personnel est décrit dans <Privacy />, qui précise
            notamment les finalités, les bases légales, les destinataires, les durées de conservation, les
            transferts hors UE et tes droits (accès, rectification, effacement, portabilité, opposition,
            limitation). Ces droits s’exercent auprès de <Mail />. Tu disposes également du droit d’introduire
            une réclamation auprès de la CNIL (www.cnil.fr).
          </P>
        </Section>

        <Section title="Article 9 - Disponibilité, évolutions et maintenance">
          <P>
            L’Éditeur s’efforce d’assurer un accès continu à l’Application, sans garantie de disponibilité
            ininterrompue. Le service peut être temporairement suspendu pour maintenance, mise à jour, ou en cas
            d’événement indépendant de la volonté de l’Éditeur (défaillance d’un hébergeur, d’un opérateur, d’un
            fournisseur d’IA, cyberattaque, force majeure). L’Éditeur peut faire évoluer, modifier ou supprimer
            tout ou partie des fonctionnalités. En cas de suppression d’une fonctionnalité substantielle d’une
            offre payante, tu pourras résilier et obtenir le remboursement au prorata de la période non
            consommée.
          </P>
        </Section>

        <Section title="Article 10 - Suspension et résiliation">
          <Text style={styles.subTitle}>10.1 Par toi</Text>
          <P>
            Tu peux supprimer ton compte à tout moment depuis les réglages. La suppression entraîne l’effacement
            de tes données dans les conditions et délais prévus par <Privacy />.
          </P>
          <Text style={styles.subTitle}>10.2 Par l’Éditeur</Text>
          <P>
            L’Éditeur peut suspendre ou résilier ton compte, après mise en demeure restée sans effet pendant 7
            jours - ou immédiatement en cas de manquement grave (fraude, atteinte à la sécurité, contenu
            illicite, non-respect de l’âge minimum) - en cas de violation des présentes CGU. La décision te sera
            notifiée et motivée, et tu pourras la contester à l’adresse <Mail />, ta contestation faisant
            l’objet d’un réexamen humain.
          </P>
        </Section>

        <Section title="Article 11 - Responsabilité">
          <P>
            L’Éditeur est responsable des dommages directs et prévisibles résultant d’un manquement de sa part à
            ses obligations. L’Éditeur ne saurait être tenu responsable :
          </P>
          <Bullets
            items={[
              'des décisions que tu prends sur la base des Résultats IA, ni de leurs conséquences sur tes candidatures ;',
              'de l’inexactitude des Résultats IA dès lors que leur caractère indicatif et la nécessité de les vérifier ont été portés à ta connaissance (article 3) ;',
              'de l’absence de réponse, d’entretien ou d’embauche ;',
              'de la teneur des informations que tu fournis ;',
              'des dysfonctionnements imputables à ton terminal, à ta connexion, ou aux plateformes tierces (LinkedIn, stores, fournisseurs d’IA) ;',
              'des cas de force majeure.',
            ]}
          />
          <P>
            Aucune stipulation des présentes CGU n’a pour effet d’exclure ou de limiter la responsabilité de
            l’Éditeur en cas de faute lourde, de dol, de dommage corporel, ou dans les cas où la loi l’interdit.
            Les garanties légales de conformité des contenus et services numériques (art. L224-25-12 et suivants du Code de la
            consommation) et des vices cachés (art. 1641 du Code civil) demeurent en tout état de cause applicables.
          </P>
        </Section>

        <Section title="Article 12 - Signalement de contenu illicite">
          <P>
            Tout contenu illicite constaté sur l’Application peut être signalé à <Mail />. L’Éditeur en
            accusera réception et traitera le signalement avec diligence, conformément au Règlement (UE)
            2022/2065 sur les services numériques (DSA).
          </P>
        </Section>

        <Section title="Article 13 - Modification des CGU">
          <P>
            L’Éditeur peut modifier les présentes CGU. Toute modification substantielle te sera notifiée par
            email et/ou notification in-app. La poursuite de l’utilisation de l’Application après cette
            notification vaut acceptation. À défaut d’acceptation, tu peux résilier ton compte sans frais.
          </P>
        </Section>

        <Section title="Article 14 - Dispositions diverses">
          <Bullets
            items={[
              'Nullité partielle : si une clause était jugée nulle, les autres resteraient applicables.',
              'Non-renonciation : le fait pour l’Éditeur de ne pas se prévaloir d’un manquement ne vaut pas renonciation.',
              'Preuve : les registres informatisés de l’Éditeur sont admis comme mode de preuve, sauf preuve contraire.',
              'Langue : en cas de traduction, la version française prévaut.',
              'Accessibilité : l’Éditeur s’efforce de rendre l’Application accessible conformément aux exigences applicables aux services numériques destinés aux consommateurs.',
            ]}
          />
        </Section>

        <Section title="Article 15 - Droit applicable et litiges">
          <P>
            Les présentes CGU sont soumises au droit français. En cas de litige, tu es invité(e) à contacter
            d’abord <Mail /> afin de rechercher une solution amiable. Conformément à l’article L612-1 du Code de
            la consommation, tu peux recourir gratuitement à un médiateur de la consommation en vue de la
            résolution amiable de tout litige. À défaut de résolution amiable, le litige sera porté devant les
            juridictions compétentes conformément aux règles de droit commun. En qualité de consommateur, tu
            peux saisir la juridiction du lieu de ton domicile.
          </P>
        </Section>

        <Section title="Annexe - Formulaire de rétractation">
          <P>
            (À compléter et renvoyer uniquement si tu souhaites te rétracter du contrat.)
          </P>
          <P>
            À l’attention d’Alternjob (<Mail />) : je notifie par la présente ma rétractation du contrat portant
            sur la fourniture du service suivant : [service]
          </P>
          <P>Commandé le : [date] - Nom : [nom] - Date : [date]</P>
        </Section>

        <Text testID="terms-version" style={styles.version}>
          Version 1.12 - Dernière mise à jour : 28/10/2026
        </Text>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, gap: spacing.lg, paddingBottom: spacing.xxl },
  // Écran large : colonne de lecture centrée, pas une ligne de texte de 1200 px.
  contentL: { width: '100%', maxWidth: 820, alignSelf: 'center', padding: spacing.xl, paddingBottom: spacing.xxl },
  docTitle: { fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.night },
  docSubtitle: { fontSize: typography.small.size, color: colors.grey500 },
  section: { gap: spacing.sm },
  sectionTitle: { fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.grey900 },
  subTitle: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.grey900, marginTop: spacing.xs },
  paragraph: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
  bullets: { gap: spacing.xs },
  bulletRow: { flexDirection: 'row', gap: spacing.sm },
  bulletDot: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey500 },
  bulletText: { flex: 1, fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
  link: { color: colors.electric, fontWeight: '700' },
  version: { fontSize: typography.small.size, color: colors.grey500, marginTop: spacing.md },
});
